import {inject, Injectable} from '@angular/core';
import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse, HttpInterceptorFn, HttpHandlerFn,
  HttpContextToken
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TokenService } from '../token.service';
import { MerchantService } from '../merchant.service';
import { environment } from '../../../../environments/environment';
import {AuthService} from './auth.service';


/**
 * Mark requests that should retry once on auth failure (e.g., submit).
 * Usage: http.post(url, body, { context: new HttpContext().set(RETRY_ON_AUTH_FAILURE, true) })
 */
export const RETRY_ON_AUTH_FAILURE = new HttpContextToken<boolean>(() => false);

/**
 * Internal marker to avoid infinite loops.
 */
const RETRY_ALREADY_ATTEMPTED = new HttpContextToken<boolean>(() => false);

/**
 * Helper: decide what the backend signals as "invalid/expired token".
 * Adjust to your API (e.g., check status === 401 or a custom error code in the body).
 */
function isAuthInvalid(err: HttpErrorResponse): boolean {
  // Common cases:
  if (err.status === 401 || err.status === 403) return true;

  // Optional: custom backend contract example
  // return !!(err.error && (err.error.code === 'TOKEN_EXPIRED' || err.error.code === 'TOKEN_INVALID'));
  return false;
}

/**
 * Functional interceptor:
 * 1) Adds Authorization header if an in-memory token exists.
 * 2) If the request opted-in (RETRY_ON_AUTH_FAILURE) and the backend says token is invalid/expired,
 *    then fetch a new token (in-memory) and retry the request ONCE with the new token.
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const auth = inject(AuthService);
  const token = auth.getAccessToken(); // in-memory only

  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      // Only retry for opted-in requests (e.g., Submit), and ensure we didn't retry already
      const optedIn = req.context.get(RETRY_ON_AUTH_FAILURE);
      const alreadyRetried = req.context.get(RETRY_ALREADY_ATTEMPTED);

      if (!optedIn || alreadyRetried || !isAuthInvalid(err)) {
        return throwError(() => err);
      }

      // Re-check/fetch a new token (do NOT store in local/session storage).
      // Implement this in AuthService to return an Observable<string> with the fresh token.
      return auth.fetchNewAccessToken().pipe(
        switchMap((newToken) => {
          // Update the header and mark as retried
          const retriedReq = req.clone({
            context: req.context.set(RETRY_ALREADY_ATTEMPTED, true),
            setHeaders: newToken ? { Authorization: `Bearer ${newToken}` } : {}
          });
          return next(retriedReq);
        }),
        // If refresh fails, surface the original error
        catchError(() => throwError(() => err))
      );
    })
  );
};
